// Data table with a polished look. columns: [{ key, label, align, render }].
// Pass `pageSize` to paginate; pass `searchable` (with optional `searchPlaceholder`)
// to show a filter box that matches across all columns.
import { useState, Fragment } from 'react';

export function Table({
  columns, rows, empty = 'No data', pageSize, searchable, searchPlaceholder = 'Search…',
  // Pass `expandedContent(row)` to make rows clickable — clicking one toggles an inline
  // panel underneath it. `rowKey(row)` gives a stable id (defaults to the row index).
  expandedContent, rowKey,
}) {
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(null);

  const q = query.trim().toLowerCase();
  const filtered = (searchable && q)
    ? (rows || []).filter((r) => columns.some((c) => String(r[c.key] ?? '').toLowerCase().includes(q)))
    : (rows || []);
  rows = filtered;
  const total = rows ? rows.length : 0;
  const totalPages = pageSize ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const cur = Math.min(page, totalPages - 1);
  const start = pageSize ? cur * pageSize : 0;
  const view = pageSize ? (rows || []).slice(start, start + pageSize) : (rows || []);

  return (
    <div>
      {searchable && (
        <div className="mb-3">
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(0); }}
            placeholder={searchPlaceholder}
            className="w-full sm:w-72 rounded-lg border border-slate-200 dark:border-white/15 bg-white/70 dark:bg-slate-800/70 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-brand focus:bg-white dark:focus:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-brand/30"
          />
        </div>
      )}
      <div className="overflow-hidden rounded-lg border border-white/50 dark:border-white/10">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white/50 dark:bg-white/5 text-slate-500 dark:text-slate-400">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`py-2.5 px-4 text-xs font-semibold uppercase tracking-wide ${
                    c.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/40 dark:divide-white/5">
            {total === 0 ? (
              <tr><td colSpan={columns.length} className="py-8 text-center text-slate-400 dark:text-slate-500">{empty}</td></tr>
            ) : (
              view.map((row, i) => {
                const key = rowKey ? rowKey(row) : start + i;
                const isOpen = expandedContent && expanded === key;
                return (
                  <Fragment key={key}>
                    <tr
                      onClick={expandedContent ? () => setExpanded(isOpen ? null : key) : undefined}
                      className={`transition-colors hover:bg-brand/10 dark:hover:bg-white/5 ${expandedContent ? 'cursor-pointer' : ''} ${isOpen ? 'bg-brand/10 dark:bg-white/5' : (start + i) % 2 ? 'bg-white/25 dark:bg-white/[0.03]' : 'bg-transparent'}`}
                    >
                      {columns.map((c, ci) => (
                        <td
                          key={c.key}
                          className={`py-2.5 px-4 ${
                            c.align === 'right' ? 'text-right tabular-nums' : ''
                          } ${ci === 0 ? 'font-medium text-navy-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}
                        >
                          {ci === 0 && expandedContent && (
                            <span className={`mr-2 inline-block text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}>›</span>
                          )}
                          {c.render ? c.render(row[c.key], row) : row[c.key]}
                        </td>
                      ))}
                    </tr>
                    {isOpen && (
                      <tr className="bg-brand/[0.06] dark:bg-white/[0.04]">
                        <td colSpan={columns.length} className="px-4 pb-4 pt-1">
                          {expandedContent(row)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pageSize && totalPages > 1 && (
        <div className="flex items-center justify-between gap-3 border-t border-white/40 dark:border-white/10 bg-white/40 dark:bg-white/5 px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400">
          <span>Showing {start + 1}–{Math.min(start + pageSize, total)} of {total}</span>
          <div className="flex items-center gap-2">
            <button
              type="button" disabled={cur === 0} onClick={() => setPage(cur - 1)}
              className="rounded-md border border-slate-200 dark:border-white/15 bg-white/70 dark:bg-white/10 px-2.5 py-1 font-medium text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Prev
            </button>
            <span>Page {cur + 1} / {totalPages}</span>
            <button
              type="button" disabled={cur >= totalPages - 1} onClick={() => setPage(cur + 1)}
              className="rounded-md border border-slate-200 dark:border-white/15 bg-white/70 dark:bg-white/10 px-2.5 py-1 font-medium text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
